// What a failed submit actually means — the rules, with no DOM and no network
// attached (same split as ladder.ts vs the session that calls it).
//
// Before H01 every failure read as 「服务端没能核对上这一局」 and only a status-0
// failure was cached for retry. That copy is a confession the server usually
// never made: a 404 or a 502 means the run never reached verification at all,
// and throwing it away loses a real score to what is almost always a transient
// server-side condition. Telling a player their run was refused when it was
// merely undelivered is the worst of the three outcomes — it reads as an
// accusation, and design §4.5 already promises the run is kept.

/** Which `result.*` line the screen shows, and whether the run is worth keeping. */
export interface SubmitFailure {
  /** i18n key for the note under the score. */
  note: 'result.offline' | 'result.rejected' | 'result.unsaved';
  /** Cache the body for `flushPendingRun()` on the next boot (design §4.5)? */
  retry: boolean;
  /**
   * Does this failure spend one of the `PENDING_RUN_MAX_TRIES`? Only failures
   * the SERVER attributed do. A request that never reached a server (status 0)
   * is no evidence the run is doomed, and burning the budget on it discards a
   * valid run after three offline app-opens — the regression IR-H01 caught.
   */
  consumesBudget: boolean;
}

/**
 * `status` is `ApiError.status`: 0 when `fetch` itself threw (offline, DNS,
 * aborted), otherwise the HTTP status. Anything that is not a status at all
 * (a negative sentinel for a non-`ApiError` throw) is treated as final: the
 * only way to reach it is a throw that did not come from the request, so the
 * run may already be stored and must never be resubmitted.
 *
 * - **0** — the network, not the server. Cache and say so.
 * - **400 / 422** — the server read this exact run and refused it. Retrying
 *   replays the same stream to the same verifier, so the answer cannot change:
 *   this is the only case where 「没能核对上」 is true. 400 also covers
 *   `invalid_json` and `missing_device_id`, which are the client's fault
 *   rather than the run's; both are unreachable from a shipped build and
 *   neither improves on a retry, so they share the same final verdict.
 * - **anything else** (404 unknown_track, 408, 429, 5xx, a proxy answering
 *   mid-deploy) — the run was never stored. Keep it; the next boot resends it.
 */
/**
 * How many times a cached run may be resent before it is written off. A run
 * whose week is gone (booted Sunday, submitted Monday, DB never had that row)
 * can never succeed, and the pending slot holds exactly one run — an unbounded
 * retry would resubmit the same doomed body on every boot for good.
 */
export const PENDING_RUN_MAX_TRIES = 3;

/** Keep a cached run for another boot? `tries` = attempts already spent. */
export function shouldKeepPending(status: number, tries: number): boolean {
  const f = classifySubmitFailure(status);
  if (!f.retry) return false;
  // An offline attempt costs nothing and proves nothing: keep the run without
  // spending a try. A doomed run (its week gone) still gets a server answer —
  // a 404 — so the budget still terminates.
  return !f.consumesBudget || tries + 1 < PENDING_RUN_MAX_TRIES;
}

export function classifySubmitFailure(status: number): SubmitFailure {
  if (status === 0) return { note: 'result.offline', retry: true, consumesBudget: false };
  // A non-ApiError throw is not a server judgment: the request may have
  // succeeded and thrown afterwards, so the run might already be stored. We
  // must not resubmit it — but we must not accuse the server of refusing it
  // either. 「服务端没能核对上这一局」 is reserved for a verdict the server
  // actually gave (IR-H01, GLM seat: the same false-accusation class this
  // round exists to remove).
  if (status < 0) return { note: 'result.unsaved', retry: false, consumesBudget: true };
  if (status === 400 || status === 422)
    return { note: 'result.rejected', retry: false, consumesBudget: true };
  return { note: 'result.unsaved', retry: true, consumesBudget: true };
}
